// Запросы «Сегодня»: /api/today, /api/onboarding и быстрый ввод (через API других областей).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'
import type { Goal } from '@/pages/goals/api'
import type { Condition, Supplement } from '@/pages/health-journal/api'
import type { Planned } from '@/pages/plan/api'

export type Sleep = {
  day: string
  duration_s: number | null
  deep_s: number | null
  light_s: number | null
  rem_s: number | null
  awake_s: number | null
  score: number | null
}

export type OnboardingItem = { key: 'garmin' | 'weight' | 'template' | 'goal'; label: string; done: boolean; path: string }
export type Onboarding = { items: OnboardingItem[]; dismissed: boolean; visible: boolean }

export type Today = {
  day: string
  sleep: Sleep | null
  readiness: { readiness: number | null; body_battery_max: number | null; body_battery_min: number | null } | null
  steps: { steps: number; goal: number | null } | null
  weight: { value: number; day: string; trend_7d: number | null } | null
  wellbeing: { wellbeing: number; energy: number | null } | null
  plan: Planned[]
  goals: Goal[]
  conditions: Condition[]
  supplements: Supplement[]
  onboarding: Onboarding
}

export const todayKeys = {
  all: ['today'] as const,
}

export const todayApi = {
  get: () => api.get<Today>('/today'),
  dismissOnboarding: () => api.post<Onboarding>('/onboarding/dismiss'),
  addWeight: (value: number) =>
    api.post('/metrics/samples', { metric: 'weight', value, measured_at: null, note: null }),
  addPressure: (systolic: number, diastolic: number, pulse: number | null) =>
    api.post('/blood-pressure', { systolic, diastolic, pulse, arm: null, note: null, measured_at: null }),
  saveWellbeing: (day: string, wellbeing: number, energy: number | null) =>
    api.put(`/wellbeing/${day}`, { wellbeing, energy, note: null }),
  saveNutrition: (day: string, data: Record<string, number | null>) => api.put(`/nutrition/${day}`, data),
  setIntake: (id: string, day: string, taken: boolean) =>
    taken ? api.put(`/supplements/${id}/intake/${day}`) : api.delete(`/supplements/${id}/intake/${day}`),
}

export function useToday() {
  return useQuery({ queryKey: todayKeys.all, queryFn: todayApi.get })
}

/** Запись с «Сегодня» сбрасывает сводку и кэш областей, куда записали (графики, журнал). */
export function useTodayMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: todayKeys.all }),
        client.invalidateQueries({ queryKey: ['metrics'] }),
        client.invalidateQueries({ queryKey: ['health'] }),
        client.invalidateQueries({ queryKey: ['changes'] }),
        client.invalidateQueries({ queryKey: ['goals'] }),
      ]),
  })
}
